// AT-7 (2026-06-09) — Página global de Atestados (módulo Comercial).
// Consolida os atestados de TODOS os contratos com 2 visualizações:
//   • Lista — tabela com checklist de documentos + status do contrato
//   • Kanban — 9 colunas de status (como no Notion), drag-and-drop
// Também mostra "Contratos sem atestado" com a prontidão de documentos,
// e permite upload de minuta (.docx) / atestado assinado (.pdf) externos.

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, Card, Collapse, Empty, Flex, Grid, Input, Segmented,
  Space, Spin, Table, Tag, Tooltip, Typography, Dropdown,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined, BarsOutlined, CheckCircleFilled, CloseCircleFilled,
  DownloadOutlined, FileWordOutlined, FilePdfOutlined, ReloadOutlined,
  SearchOutlined, PaperClipOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtDataBr } from '../utils/dataFmt';
import { useColunasRedimensionaveis } from '../utils/colunasRedimensionaveis';
import ModalAtestado from './operacional-contratos/ModalAtestado';
import { STATUS_ATESTADO, statusCfg } from './operacional-contratos/atestadoStatus';

const { Text, Title } = Typography;

interface AtestadoGlobal {
  id: string;
  numero: string | null;
  status: string;
  contrato_id: string | null;
  data_emissao: string | null;
  criado_em: string;
  atualizado_em: string;
  cat_numero: string | null;
  pdf_final_s3_key: string | null;
  anexo_minuta_s3_key: string | null;
  anexo_minuta_nome: string | null;
  anexo_assinado_s3_key: string | null;
  anexo_assinado_nome: string | null;
  // AT-8 (Notion import)
  titulo: string | null;
  tags: string[] | null;
  revisor: string | null;
  protocolo_crea: string | null;
  notion_page_id: string | null;
  anexo_timbrado_s3_key: string | null;
  anexo_timbrado_nome: string | null;
  anexos_extras: Array<{ categoria: string; nome: string; s3_key: string }> | null;
  cat_anexo_s3_key: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  contrato_status: string | null;
  data_termino_real: string | null;
  doc_contrato_assinado: boolean;
  cliente_razao_social: string | null;
  doc_qtd_arts: number;
  doc_qtd_pdf_contrato: number;
  doc_qtd_relatorios: number;
}

// Rótulo do atestado: numero do sistema ou título livre vindo do Notion
function rotulo(a: AtestadoGlobal): string {
  return a.numero || a.titulo || '(sem número)';
}

interface ContratoProntidao {
  id: string;
  numero: string | null;
  titulo: string | null;
  contrato_status: string | null;
  data_termino_real: string | null;
  doc_contrato_assinado: boolean;
  cliente_razao_social: string | null;
  doc_qtd_arts: number;
  doc_qtd_pdf_contrato: number;
  doc_qtd_relatorios: number;
}

// ─── Checklist de documentos (✓/✗ por requisito) ───────────────────────────
function ChecklistDocs({ row, compacto }: { row: AtestadoGlobal | ContratoProntidao; compacto?: boolean }) {
  const itens = [
    { ok: !!row.doc_contrato_assinado, label: 'Contrato assinado' },
    { ok: (row.doc_qtd_arts || 0) > 0, label: `ART (${row.doc_qtd_arts || 0})` },
    { ok: (row.doc_qtd_pdf_contrato || 0) > 0, label: 'PDF do contrato no Drive' },
    { ok: (row.doc_qtd_relatorios || 0) > 0, label: `Relatório no Drive (${row.doc_qtd_relatorios || 0})` },
  ];
  return (
    <Space size={compacto ? 4 : 8} wrap>
      {itens.map(i => (
        <Tooltip key={i.label} title={i.label}>
          <span style={{ fontSize: compacto ? 10 : 11, whiteSpace: 'nowrap' }}>
            {i.ok
              ? <CheckCircleFilled style={{ color: '#52c41a' }} />
              : <CloseCircleFilled style={{ color: '#d9d9d9' }} />}
            {!compacto && <span style={{ marginLeft: 3, color: i.ok ? '#444' : '#999' }}>{i.label}</span>}
          </span>
        </Tooltip>
      ))}
    </Space>
  );
}

function TagContratoStatus({ status, terminoReal }: { status: string | null; terminoReal: string | null }) {
  const finalizado = status === 'finalizado' || !!terminoReal;
  return finalizado
    ? <Tag color="green" style={{ fontSize: 10 }}>Finalizado</Tag>
    : <Tag style={{ fontSize: 10 }}>{status === 'em_andamento' ? 'Em andamento' : (status || 'ativo')}</Tag>;
}

// Ícones dos anexos — um só componente para a Lista e o Kanban. Antes cada
// vista tinha a sua cópia e discordavam: a Lista omitia o timbrado e o Kanban
// omitia a CAT.
function IconesAnexos({ a }: { a: AtestadoGlobal }) {
  return (
    <Space size={4}>
      {a.anexo_minuta_s3_key && (
        <Tooltip title={`Minuta: ${a.anexo_minuta_nome || 'arquivo'}`}>
          <FileWordOutlined style={{ color: '#1677ff', fontSize: 12 }} />
        </Tooltip>
      )}
      {a.anexo_assinado_s3_key && (
        <Tooltip title={`Assinado: ${a.anexo_assinado_nome || 'arquivo'}`}>
          <FilePdfOutlined style={{ color: '#cf1322', fontSize: 12 }} />
        </Tooltip>
      )}
      {a.anexo_timbrado_s3_key && (
        <Tooltip title="Timbrado">
          <FilePdfOutlined style={{ color: '#389e0d', fontSize: 12 }} />
        </Tooltip>
      )}
      {a.cat_numero && (
        <Tooltip title={`CAT ${a.cat_numero}`}>
          <Tag color="success" style={{ fontSize: 9, margin: 0 }}>CAT</Tag>
        </Tooltip>
      )}
    </Space>
  );
}

// Soma das larguras das colunas que aparecem no breakpoint atual. É o piso do
// scroll.x: número menor que a soma esmaga a coluna sem width (foi assim que
// "HY25012" quebrou letra a letra — 1075 de colunas fixas num scroll.x de 1100).
function somarLarguras(cols: ColumnsType<any>, bp: Record<string, boolean | undefined>): number {
  return cols.reduce((soma, c: any) => {
    const resp: string[] | undefined = c.responsive;
    const visivel = !resp || resp.some(b => !!bp[b]);
    return visivel ? soma + (Number(c.width) || 0) : soma;
  }, 0);
}

function ComercialAtestadosInner() {
  const { message } = AntApp.useApp();
  const bp = Grid.useBreakpoint();
  const { aplicar, componentes, restaurar, ajustado } =
    useColunasRedimensionaveis<AtestadoGlobal>('com:atestados:larguras');
  const [lista, setLista] = useState<AtestadoGlobal[]>([]);
  const [prontidao, setProntidao] = useState<ContratoProntidao[]>([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState<'lista' | 'kanban'>(
    () => (sessionStorage.getItem('atestados_vista') as any) || 'kanban');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<AtestadoGlobal | null>(null);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  // Upload de anexo externo: qual atestado + tipo está em andamento
  const [uploading, setUploading] = useState<string | null>(null);
  const uploadCtx = useRef<{ id: string; tipo: 'minuta' | 'assinado' } | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_atestados_globais_listar' },
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_atestados_contratos_prontidao' },
          { headers: { Authorization: token } }),
      ]);
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setLista(Array.isArray(d1) ? d1 : []);
      setProntidao(Array.isArray(d2) ? d2 : []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  function setVistaPersist(v: 'lista' | 'kanban') {
    setVista(v);
    sessionStorage.setItem('atestados_vista', v);
  }

  async function mudarStatus(id: string, novo: string) {
    const at = lista.find(a => a.id === id);
    if (!at || at.status === novo) return;
    // Otimista: move já na UI, reverte se falhar
    const anterior = at.status;
    setLista(ls => ls.map(a => a.id === id ? { ...a, status: novo } : a));
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_atualizar_status', id, status: novo },
        { headers: { Authorization: token } });
      message.success(`${at.numero || 'Atestado'} → ${statusCfg(novo).label}`);
    } catch (e: any) {
      setLista(ls => ls.map(a => a.id === id ? { ...a, status: anterior } : a));
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  // ─── Upload de anexo externo (minuta .docx / assinado .pdf) ──────────────
  function iniciarUpload(id: string, tipo: 'minuta' | 'assinado') {
    uploadCtx.current = { id, tipo };
    if (inputFileRef.current) {
      inputFileRef.current.accept = tipo === 'minuta' ? '.docx,.doc,.pdf' : '.pdf';
      inputFileRef.current.click();
    }
  }

  async function processarUpload(file: File) {
    const ctx = uploadCtx.current;
    if (!ctx) return;
    setUploading(ctx.id);
    try {
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_anexo_upload_url', id: ctx.id, tipo: ctx.tipo,
          filename: file.name, content_type: file.type || 'application/octet-stream' },
        { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      await axios.put(d1.upload_url, file,
        { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_anexo_confirmar', id: ctx.id, tipo: ctx.tipo,
          s3_key: d1.s3_key, nome: file.name },
        { headers: { Authorization: token } });
      message.success(`${ctx.tipo === 'minuta' ? 'Minuta' : 'Atestado assinado'} anexado: ${file.name}`);
      carregar();
    } catch (e: any) {
      message.error('Falha no upload: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploading(null);
      uploadCtx.current = null;
    }
  }

  async function baixarAnexo(id: string, tipo: string, s3Key?: string) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_anexo_url', id, tipo, s3_key: s3Key },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) window.open(d.url, '_blank', 'noopener');
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  function menuAnexos(a: AtestadoGlobal) {
    const items: any[] = [
      { key: 'up-minuta', icon: <FileWordOutlined />, label: 'Anexar minuta (.docx/.pdf)',
        onClick: () => iniciarUpload(a.id, 'minuta') },
      { key: 'up-assinado', icon: <FilePdfOutlined />, label: 'Anexar assinado (.pdf)',
        onClick: () => iniciarUpload(a.id, 'assinado') },
    ];
    if (a.anexo_minuta_s3_key) items.push({
      key: 'dl-minuta', icon: <DownloadOutlined />,
      label: `Baixar minuta — ${a.anexo_minuta_nome || 'arquivo'}`,
      onClick: () => baixarAnexo(a.id, 'minuta'),
    });
    if (a.anexo_assinado_s3_key) items.push({
      key: 'dl-assinado', icon: <DownloadOutlined />,
      label: `Baixar assinado — ${a.anexo_assinado_nome || 'arquivo'}`,
      onClick: () => baixarAnexo(a.id, 'assinado'),
    });
    // AT-8: timbrado, CAT e anexos extras (Arquivos base / Relatório
    // Correções importados do Notion)
    if (a.anexo_timbrado_s3_key) items.push({
      key: 'dl-timbrado', icon: <DownloadOutlined />,
      label: `Baixar timbrado — ${a.anexo_timbrado_nome || 'arquivo'}`,
      onClick: () => baixarAnexo(a.id, 'timbrado'),
    });
    if (a.cat_anexo_s3_key) items.push({
      key: 'dl-cat', icon: <DownloadOutlined />,
      label: `Baixar CAT${a.cat_numero ? ` (${a.cat_numero})` : ''}`,
      onClick: () => baixarAnexo(a.id, 'cat'),
    });
    (a.anexos_extras || []).forEach((ex, i) => {
      items.push({
        key: `dl-extra-${i}`, icon: <DownloadOutlined />,
        label: `${ex.categoria === 'correcoes' ? 'Correções' : 'Base'} — ${ex.nome}`,
        onClick: () => baixarAnexo(a.id, 'extra', ex.s3_key),
      });
    });
    if (a.pdf_final_s3_key) items.push({
      key: 'dl-final', icon: <DownloadOutlined />, label: 'Baixar PDF gerado pelo sistema',
      onClick: () => baixarAnexo(a.id, 'pdf_final'),
    });
    return { items };
  }

  const filtradas = lista.filter(a => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (a.numero || '').toLowerCase().includes(q)
      || (a.titulo || '').toLowerCase().includes(q)
      || (a.contrato_numero || '').toLowerCase().includes(q)
      || (a.contrato_titulo || '').toLowerCase().includes(q)
      || (a.cliente_razao_social || '').toLowerCase().includes(q)
      || (a.tags || []).some(t => t.toLowerCase().includes(q));
  });

  // ─── Vista LISTA ───────────────────────────────────────────────────────────
  // Toda coluna tem width e a tabela é tableLayout="fixed": largura sem layout
  // fixo é sugestão, e a coluna que ficava sem width (Contrato) era espremida
  // até o número quebrar letra a letra. Texto longo usa ellipsis + nowrap, e o
  // título inteiro fica no tooltip.
  const colunas: ColumnsType<AtestadoGlobal> = [
    {
      title: 'Atestado', key: 'numero', width: 200,
      render: (_: any, a: AtestadoGlobal) => (
        <Tooltip title={a.titulo || undefined}>
          <div>
            <Text strong ellipsis style={{ color: '#020D7F', fontSize: 12, display: 'block', whiteSpace: 'nowrap' }}>
              {rotulo(a)}
            </Text>
            {a.revisor && (
              <Text type="secondary" ellipsis style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>
                Revisor: {a.revisor}
              </Text>
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Contrato', key: 'contrato', width: 260,
      render: (_: any, a: AtestadoGlobal) => a.contrato_id ? (
        <Tooltip title={a.contrato_titulo || undefined}>
          <div>
            <Text strong ellipsis style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap' }}>
              {a.contrato_numero}
            </Text>
            <Text type="secondary" ellipsis style={{ fontSize: 11, display: 'block' }}>
              {a.contrato_titulo}
            </Text>
          </div>
        </Tooltip>
      ) : <Text type="secondary" italic style={{ fontSize: 11 }}>sem contrato no sistema</Text>,
    },
    {
      title: 'Cliente', dataIndex: 'cliente_razao_social', key: 'cliente_razao_social',
      width: 190, ellipsis: true, responsive: ['md'],
      render: (v: string | null) => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 140,
      filters: STATUS_ATESTADO.map(s => ({ text: s.label, value: s.key })),
      onFilter: (val: any, a: AtestadoGlobal) => a.status === val,
      render: (s: string) => <Tag color={statusCfg(s).tagColor}>{statusCfg(s).label}</Tag>,
    },
    {
      title: 'Situação do contrato', key: 'ct_status', width: 120, responsive: ['md'],
      render: (_: any, a: AtestadoGlobal) =>
        <TagContratoStatus status={a.contrato_status} terminoReal={a.data_termino_real} />,
    },
    {
      title: 'Docs p/ atestado', key: 'docs', width: 100, responsive: ['lg'],
      render: (_: any, a: AtestadoGlobal) => a.contrato_id
        ? <ChecklistDocs row={a} compacto />
        : <Text type="secondary" style={{ fontSize: 10 }}>—</Text>,
    },
    {
      title: 'Anexos', key: 'anexos', width: 96, responsive: ['md'],
      render: (_: any, a: AtestadoGlobal) => <IconesAnexos a={a} />,
    },
    {
      title: 'Atualizado', dataIndex: 'atualizado_em', key: 'atualizado_em', width: 96, responsive: ['lg'],
      sorter: (a: AtestadoGlobal, b: AtestadoGlobal) =>
        new Date(a.atualizado_em || 0).getTime() - new Date(b.atualizado_em || 0).getTime(),
      render: (v: string) => <Text style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v ? fmtDataBr(v) : '—'}</Text>,
    },
    {
      title: '', key: 'acoes', width: 44,
      render: (_: any, a: AtestadoGlobal) => (
        <Dropdown menu={menuAnexos(a)} placement="bottomRight" trigger={['click']}>
          <Button size="small" type="text" icon={<PaperClipOutlined />}
            loading={uploading === a.id}
            onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];
  // Larguras já com o ajuste do usuário (arrasto persistido) — a soma para o
  // scroll.x tem que ser sobre ESSAS, senão puxar uma coluna para 400px deixa a
  // tabela menor que as colunas e volta a espremer a última.
  const colunasAplicadas = aplicar(colunas);
  const scrollXLista = somarLarguras(colunasAplicadas, bp);

  // ─── Vista KANBAN ──────────────────────────────────────────────────────────
  const kanban = (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${STATUS_ATESTADO.length}, minmax(210px, 1fr))`,
        gap: 10, alignItems: 'flex-start', minWidth: STATUS_ATESTADO.length * 220,
      }}>
        {STATUS_ATESTADO.map(st => {
          const itens = filtradas.filter(a => a.status === st.key);
          const isHover = hoverCol === st.key;
          return (
            <div key={st.key}
              onDragOver={e => { e.preventDefault(); setHoverCol(st.key); }}
              onDragLeave={() => setHoverCol(null)}
              onDrop={e => {
                e.preventDefault();
                if (arrastando) mudarStatus(arrastando, st.key);
                setArrastando(null); setHoverCol(null);
              }}>
              <Card size="small"
                style={{
                  minHeight: 240,
                  background: isHover ? '#F0F5FF' : undefined,
                  borderColor: isHover ? st.hex : undefined,
                  transition: 'background 0.15s, border 0.15s',
                }}
                title={
                  <Text strong style={{
                    color: st.hex, fontSize: 10, textTransform: 'uppercase',
                    letterSpacing: 0.5, whiteSpace: 'nowrap',
                  }}>
                    {st.label} ({itens.length})
                  </Text>
                }
                styles={{
                  header: { borderBottom: `2px solid ${st.hex}`, padding: '4px 10px', minHeight: 0 },
                  body: { padding: 8 },
                }}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {itens.length === 0 && (
                    <Text type="secondary" italic style={{ fontSize: 10, display: 'block', textAlign: 'center', padding: '16px 4px' }}>
                      Solte aqui
                    </Text>
                  )}
                  {itens.map(a => (
                    <Card key={a.id} size="small" hoverable draggable
                      onDragStart={() => setArrastando(a.id)}
                      onClick={() => setAberto(a)}
                      style={{
                        borderLeft: `3px solid ${st.hex}`,
                        boxShadow: arrastando === a.id ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
                        opacity: arrastando === a.id ? 0.6 : 1,
                        cursor: 'grab',
                      }}
                      styles={{ body: { padding: 8 } }}>
                      <Text strong style={{ fontSize: 11, color: '#020D7F', display: 'block' }}>
                        {rotulo(a)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 10, display: 'block' }} ellipsis>
                        {a.cliente_razao_social || a.contrato_titulo || a.contrato_numero || 'sem vínculo'}
                      </Text>
                      {(a.tags || []).length > 0 && (
                        <Tooltip title={(a.tags || []).join(' · ')}>
                          <div style={{ marginTop: 4 }}>
                            {(a.tags || []).slice(0, 2).map(t => (
                              <Tag key={t} style={{ fontSize: 8, padding: '0 4px', lineHeight: '14px', marginRight: 2 }}>{t}</Tag>
                            ))}
                            {(a.tags || []).length > 2 && (
                              <Text type="secondary" style={{ fontSize: 9 }}>+{(a.tags || []).length - 2}</Text>
                            )}
                          </div>
                        </Tooltip>
                      )}
                      <Flex justify="space-between" align="center" style={{ marginTop: 6 }}>
                        {a.contrato_id ? <ChecklistDocs row={a} compacto /> : <span />}
                        <IconesAnexos a={a} />
                      </Flex>
                    </Card>
                  ))}
                </Space>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Contratos sem atestado (prontidão) ────────────────────────────────────
  const prontos = prontidao.filter(c =>
    c.doc_contrato_assinado && c.doc_qtd_arts > 0 && c.doc_qtd_pdf_contrato > 0);
  // Mesma doença da lista: Contrato sem width + Cliente com ellipsis (que força
  // layout fixo) + sem scroll.x. Aqui sem redimensionamento — é uma lista curta.
  const colunasProntidao: ColumnsType<ContratoProntidao> = [
    {
      title: 'Contrato', key: 'c', width: 260,
      render: (_: any, c: ContratoProntidao) => (
        <Tooltip title={c.titulo || undefined}>
          <div>
            <Text strong ellipsis style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap' }}>{c.numero}</Text>
            <Text type="secondary" ellipsis style={{ fontSize: 11, display: 'block' }}>{c.titulo}</Text>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Cliente', dataIndex: 'cliente_razao_social', key: 'cliente', width: 200, ellipsis: true, responsive: ['md'],
      render: (v: string | null) => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>,
    },
    {
      title: 'Status', key: 's', width: 110,
      render: (_: any, c: ContratoProntidao) =>
        <TagContratoStatus status={c.contrato_status} terminoReal={c.data_termino_real} />,
    },
    {
      title: 'Docs necessários', key: 'd', width: 300, responsive: ['lg'],
      render: (_: any, c: ContratoProntidao) => <ChecklistDocs row={c} />,
    },
    {
      title: '', key: 'a', width: 130,
      render: (_: any, c: ContratoProntidao) => (
        <Button size="small" type="link"
          onClick={() => {
            // Abre o contrato direto na aba Atestado (mesmas chaves
            // da persistência F5 de OperacionalContratos)
            sessionStorage.setItem('op:contrato_aberto', c.id);
            sessionStorage.setItem(`op:aba:${c.id}`, 'atestados');
            window.dispatchEvent(new CustomEvent('app-navigate', {
              detail: { modulo: 'operacional', pagina: 'op-contratos' },
            }));
          }}>
          Abrir contrato →
        </Button>
      ),
    },
  ];
  const scrollXProntidao = somarLarguras(colunasProntidao, bp);
  const secaoProntidao = (
    <Collapse size="small" ghost style={{ marginTop: 18 }}
      items={[{
        key: 'p',
        label: (
          <Text strong style={{ fontSize: 12, color: '#020D7F' }}>
            📋 Contratos sem atestado ({prontidao.length})
            {prontos.length > 0 && (
              <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>
                {prontos.length} pronto(s) pra iniciar
              </Tag>
            )}
          </Text>
        ),
        children: (
          <Table size="small" rowKey="id" dataSource={prontidao}
            pagination={{ pageSize: 15 }}
            columns={colunasProntidao}
            tableLayout="fixed"
            scroll={{ x: scrollXProntidao }} />
        ),
      }]} />
  );

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 14 }}>
        <Title level={4} style={{ margin: 0 }}>Atestados Técnicos</Title>
        <Space size={8} wrap>
          <Input size="small" allowClear value={busca}
            onChange={e => setBusca(e.target.value)}
            prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            placeholder="Buscar número, contrato, cliente…"
            style={{ width: 240 }} />
          <Segmented size="small" value={vista}
            onChange={(v) => setVistaPersist(v as any)}
            options={[
              { value: 'kanban', label: <Space size={4}><AppstoreOutlined />Kanban</Space> },
              { value: 'lista', label: <Space size={4}><BarsOutlined />Lista</Space> },
            ]} />
          {vista === 'lista' && ajustado && (
            <Button type="link" size="small" onClick={restaurar}>Restaurar larguras</Button>
          )}
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar} />
        </Space>
      </Flex>

      {loading ? (
        <Flex justify="center" style={{ padding: 60 }}><Spin /></Flex>
      ) : lista.length === 0 ? (
        <Empty description="Nenhum atestado ainda. Inicie pelo contrato (aba Atestado) ou veja abaixo quais contratos estão prontos." />
      ) : vista === 'kanban' ? kanban : (
        <Table size="small" rowKey="id" columns={colunasAplicadas} dataSource={filtradas}
          components={componentes} tableLayout="fixed"
          pagination={{ pageSize: 30, showSizeChanger: true }}
          onRow={(a) => ({ onClick: () => setAberto(a), style: { cursor: 'pointer' } })}
          scroll={{ x: scrollXLista }} />
      )}

      {secaoProntidao}

      {/* input escondido pro upload de anexos */}
      <input ref={inputFileRef} type="file" style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) processarUpload(f);
          e.target.value = '';
        }} />

      {aberto && (
        <ModalAtestado
          atestadoId={aberto.id}
          contrato={{ id: aberto.contrato_id || '', numero: aberto.contrato_numero || undefined }}
          onClose={() => setAberto(null)}
          onSaved={() => carregar()} />
      )}
    </div>
  );
}

export default function ComercialAtestados() {
  return <AntApp><ComercialAtestadosInner /></AntApp>;
}
